<script setup lang="ts">
import {
  getAdminListJobImportBatchesQueryKey,
  useAdminCreateJobImportBatch,
  useAdminDeleteJobImportBatch,
  useAdminListJobImportBatches,
  useLookupAdminCompanies,
} from '@swipe4work/api-orval'
import {
  AdminPermissionResource,
  type CreateJobImportBatchItemDto,
  type ListJobImportBatchesBatchDto,
  PermissionLevel,
} from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import JobImportRow from '@/features/jobs-import/components/JobImportRow.vue'
import { CSV_FIELDS, type CsvFieldMapping, resolveCsvItems } from '@/features/jobs-import/csv-import'
import { emptyJobImportItem, type JobImportDraftItem, toImportItemDto } from '@/features/jobs-import/types'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from '@/shared/composables/UseToast'

definePage({
  path: '/settings/jobs-import',
  name: '/settings/JobsImport',
  meta: {
    title: 'Vacatures importeren',
    permission: {
      resource: 'Job',
      level: 'Read',
    },
  },
})

const router = useRouter()
const toast = useToast()
const confirm = useConfirm()
const queryClient = useQueryClient()

// ── Overview of earlier runs ───────────────────────────────────────────────
const { data: batchesData, isPending: batchesPending } = useAdminListJobImportBatches({
  query: { staleTime: 0 },
})
const batches = computed(() => batchesData.value?.batches ?? [])

function resumeBatch(batch: ListJobImportBatchesBatchDto) {
  router.push(`/settings/jobs-import/${batch.id}`)
}

const { mutateAsync: deleteBatchMutation } = useAdminDeleteJobImportBatch()

function confirmDeleteBatch(batch: ListJobImportBatchesBatchDto) {
  confirm.require({
    message: `Weet je zeker dat je import "${batch.name || `#${batch.id}`}" (${batch.companyName}) wilt verwijderen? Niet-afgeronde concepten worden ook verwijderd.`,
    header: 'Import verwijderen',
    icon: 'fa-regular fa-trash',
    rejectLabel: 'Annuleer',
    acceptLabel: 'Verwijder',
    acceptClass: 'p-button-danger',
    accept: () => deleteBatch(batch),
  })
}

async function deleteBatch(batch: ListJobImportBatchesBatchDto) {
  try {
    await deleteBatchMutation({ id: batch.id })
    await queryClient.invalidateQueries({ queryKey: getAdminListJobImportBatchesQueryKey() })
    toast.success('Verwijderd', 'De import is verwijderd.')
  }
  catch {
    toast.error('Fout', 'Kon de import niet verwijderen.')
  }
}

const { data: companiesData, isPending: companiesPending } = useLookupAdminCompanies({
  query: { staleTime: 5 * 60 * 1000 },
})
const companyOptions = computed(() => companiesData.value?.companies ?? [])

const companyId = ref<number | null>(null)
const batchName = ref<string>('')

// ── Vacancy rows (each row edits a JobImportDraftItem; per-row option loading lives in JobImportRow) ──
const items = ref<JobImportDraftItem[]>([ emptyJobImportItem() ])

function addItem() {
  items.value = [ ...items.value, emptyJobImportItem() ]
}

function removeItem(index: number) {
  items.value = items.value.filter((_, i) => i !== index)
}

// Apply the first row's conversion inputs to every row — quick-fill for similar vacancies.
function applyFirstFieldsToAll() {
  const first = items.value[0]
  if (!first) return
  items.value = items.value.map(item => ({
    ...item,
    jobCategoryCode: first.jobCategoryCode,
    jobFunctionId: first.jobFunctionId,
    characteristicColor: first.characteristicColor,
    trackCategory: first.trackCategory,
    experienceLevel: first.experienceLevel,
  }))
}

// ── CSV fallback: parse an uploaded CSV and turn a chosen column into vacancy rows ──
const csvRows = ref<string[][]>([])
const csvHeaders = ref<string[]>([])
const csvMapping = ref<CsvFieldMapping>({})
const isImportingCsv = ref(false)

/**
 * Detects the delimiter from the header line — Excel-exported CSVs (esp. NL locale) commonly use ';'
 * or a tab rather than ','. Picks whichever separator occurs most in the first line.
 */
function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, (text.search(/\r?\n/) + 1) || text.length)
  const counts: Record<string, number> = {
    ';': (firstLine.match(/;/g) ?? []).length,
    ',': (firstLine.match(/,/g) ?? []).length,
    '\t': (firstLine.match(/\t/g) ?? []).length,
  }

  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
}

/** Minimal RFC4180-ish parser: handles quoted fields, embedded separators/newlines and "" escapes. */
function parseCsv(text: string): string[][] {
  const delimiter = detectDelimiter(text)
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        }
        else {
          inQuotes = false
        }
      }
      else {
        field += char
      }
    }
    else if (char === '"') {
      inQuotes = true
    }
    else if (char === delimiter) {
      row.push(field)
      field = ''
    }
    else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i++
      row.push(field)
      field = ''
      rows.push(row)
      row = []
    }
    else {
      field += char
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }

  return rows.filter(r => r.some(c => c.trim().length > 0))
}

async function handleCsvSelect(event: { files: File | File[] }) {
  const file = Array.isArray(event.files) ? event.files[0] : event.files
  if (!file) return

  const text = await file.text()
  const parsed = parseCsv(text)
  if (parsed.length < 2) {
    toast.error('CSV leeg', 'Geen databerijen gevonden in het CSV-bestand.')

    return
  }

  csvHeaders.value = parsed[0]
  csvRows.value = parsed.slice(1)

  // Best-effort auto-map columns to fields by matching the header text to a field label.
  const mapping: CsvFieldMapping = {}

  parsed[0].forEach((header, index) => {
    const normalized = header.trim().toLowerCase()
    const field = CSV_FIELDS.find(f =>
      f.label.toLowerCase().replace(' *', '') === normalized
      || f.key.toLowerCase() === normalized)
    if (field && mapping[field.key] == null) {
      mapping[field.key] = index
    }
  })
  // Fall back to the first column for the (required) job text if nothing matched.
  if (mapping.jobText == null) {
    mapping.jobText = 0
  }
  csvMapping.value = mapping
}

const csvColumnOptions = computed(() => [
  {
    label: '— Niet gemapt —',
    value: null, 
  },
  ...csvHeaders.value.map((header, index) => ({
    label: header || `Kolom ${index + 1}`,
    value: index,
  })),
])

async function importCsvColumns() {
  if (csvMapping.value.jobText == null) {
    toast.warn('Vacaturetekst vereist', 'Map minimaal de kolom met de vacaturetekst.')

    return
  }

  isImportingCsv.value = true
  try {
    const resolved = await resolveCsvItems(csvRows.value, csvMapping.value)
    const newItems = resolved.filter(i => i.jobText.trim().length > 0)
    if (newItems.length === 0) {
      toast.warn('Geen teksten', 'De gekozen kolom bevat geen vacatureteksten.')

      return
    }

    // Replace an initial empty single row, otherwise append.
    const existing = items.value.filter(i => i.jobText.trim().length > 0)

    items.value = [ ...existing, ...newItems ]
    csvRows.value = []
    csvHeaders.value = []
    csvMapping.value = {}
    toast.success('CSV geïmporteerd', `${newItems.length} vacature(s) toegevoegd. Controleer de velden per vacature.`)
  }
  catch {
    toast.error('Fout', 'Het CSV-bestand kon niet worden verwerkt.')
  }
  finally {
    isImportingCsv.value = false
  }
}

// ── Validation ────────────────────────────────────────────────────────────
const validationErrors = computed<string[]>(() => {
  const errors: string[] = []
  if (!companyId.value) {
    errors.push('Kies een bedrijf voor deze batch.')
  }
  const filled = items.value.filter(i => i.jobText.trim().length > 0)
  if (filled.length === 0) {
    errors.push('Voeg minimaal één vacature toe.')
  }
  filled.forEach((item, i) => {
    if (!item.jobCategoryCode || !item.characteristicColor || !item.trackCategory || !item.experienceLevel) {
      errors.push(`Vacature ${i + 1}: vul beroepsgroep, kleur, leerweg en ervaringsniveau in.`)
    }
  })

  return errors
})

const canSubmit = computed(() => validationErrors.value.length === 0)

const { mutateAsync, isPending: isSubmitting } = useAdminCreateJobImportBatch()

async function submit() {
  if (!companyId.value || !canSubmit.value) return

  const payloadItems: CreateJobImportBatchItemDto[] = items.value
    .filter(i => i.jobText.trim().length > 0)
    .map(toImportItemDto)

  try {
    const result = await mutateAsync({
      data: {
        companyId: companyId.value,
        name: batchName.value.trim() || null,
        items: payloadItems,
      },
    })

    toast.success('Batch aangemaakt', 'De vacatures worden nu door de AI verwerkt.')
    await router.push(`/settings/jobs-import/${result.batchId}`)
  }
  catch {
    toast.error('Fout', 'De import-batch kon niet worden aangemaakt.')
  }
}
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- ── Earlier runs ───────────────────────────────────────────────── -->
    <Card v-if="batchesPending || batches.length">
      <template #title>
        <span>Eerdere imports</span>
      </template>
      <template #content>
        <div v-if="batchesPending" class="flex flex-col gap-2">
          <Skeleton v-for="n in 3" :key="n" height="2.5rem" />
        </div>
        <DataTable v-else :value="batches" size="small" data-key="id" striped-rows>
          <Column header="Import">
            <template #body="{ data }">
              <div class="flex flex-col">
                <span class="font-medium">{{ data.name || `Import #${data.id}` }}</span>
                <span class="text-xs text-surface-500">{{ data.companyName }}</span>
              </div>
            </template>
          </Column>
          <Column header="Aangemaakt">
            <template #body="{ data }">
              <span class="text-sm">{{ new Date(data.createdAt).toLocaleString('nl-NL') }}</span>
            </template>
          </Column>
          <Column header="Voortgang">
            <template #body="{ data }">
              <div class="flex flex-wrap gap-1">
                <Tag :value="`${data.finalizedCount}/${data.totalCount} afgerond`" severity="success" class="text-xs" />
                <Tag v-if="data.pendingCount + data.convertingCount > 0" :value="`${data.pendingCount + data.convertingCount} bezig`" severity="info" class="text-xs" />
                <Tag v-if="data.draftCount > 0" :value="`${data.draftCount} concept`" severity="warn" class="text-xs" />
                <Tag v-if="data.failedCount > 0" :value="`${data.failedCount} mislukt`" severity="danger" class="text-xs" />
              </div>
            </template>
          </Column>
          <Column header="" style="width: 14rem">
            <template #body="{ data }">
              <div class="flex justify-end gap-2">
                <Button
                  label="Hervatten"
                  icon="fa-regular fa-arrow-right-to-bracket"
                  size="small"
                  severity="secondary"
                  @click="resumeBatch(data)"
                />
                <Button
                  v-permission="{ resource: AdminPermissionResource.Job, level: PermissionLevel.Delete }"
                  icon="fa-regular fa-trash"
                  size="small"
                  severity="danger"
                  text
                  @click="confirmDeleteBatch(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <!-- ── Step 1: Company + name ─────────────────────────────────────── -->
    <Card>
      <template #title>
        <span>1. Bedrijf & batch</span>
      </template>
      <template #content>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-1">
            <label for="company" class="text-sm font-semibold">Bedrijf</label>
            <Select
              input-id="company"
              v-model="companyId"
              :options="companyOptions"
              option-label="name"
              option-value="id"
              filter
              :loading="companiesPending"
              placeholder="Kies een bedrijf..."
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label for="batch-name" class="text-sm font-semibold">Batchnaam (optioneel)</label>
            <InputText id="batch-name" v-model="batchName" class="w-full" placeholder="Bijv. 'Import mei 2026'" />
          </div>
        </div>
      </template>
    </Card>

    <!-- ── Step 2: CSV upload (optional) ──────────────────────────────── -->
    <Card>
      <template #title>
        <span>2. CSV importeren (optioneel)</span>
      </template>
      <template #content>
        <div class="flex flex-col gap-3">
          <span class="text-sm text-surface-500">
            Upload een CSV en map de kolommen naar velden. Gemapte velden hebben voorrang op de JobConverter;
            leeg gelaten velden worden aangevuld door de AI. Of voeg hieronder handmatig vacatures toe.
          </span>
          <FileUpload
            v-if="csvHeaders.length === 0"
            mode="basic"
            :auto="true"
            accept=".csv,text/csv"
            :custom-upload="true"
            choose-label="Selecteer CSV"
            @uploader="handleCsvSelect"
          />
          <div v-else class="flex flex-col gap-4 border border-surface-200 rounded-lg p-4">
            <span class="text-xs text-surface-500">{{ csvRows.length }} rij(en) gevonden. Koppel elke kolom aan een veld:</span>
            <div class="grid grid-cols-1 gap-3 lg:grid-cols-3 md:grid-cols-2">
              <div v-for="field in CSV_FIELDS" :key="field.key" class="flex flex-col gap-1">
                <label class="text-xs font-semibold">{{ field.label }}</label>
                <Select
                  v-model="csvMapping[field.key]"
                  :options="csvColumnOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <Button
                label="Annuleer"
                severity="secondary"
                :disabled="isImportingCsv"
                @click="() => { csvHeaders = []; csvRows = []; csvMapping = {} }"
              />
              <Button
                label="Importeer kolommen als vacatures"
                icon="fa-regular fa-file-import"
                :loading="isImportingCsv"
                @click="importCsvColumns"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- ── Step 3: Vacancies + pre-conversion fields ──────────────────── -->
    <Card>
      <template #title>
        <div class="flex items-center justify-between">
          <span>3. Vacatures ({{ items.length }})</span>
          <div class="flex gap-2">
            <Button
              label="Vul velden in alle rijen"
              icon="fa-regular fa-clone"
              severity="secondary"
              size="small"
              @click="applyFirstFieldsToAll"
            />
            <Button label="Vacature toevoegen" icon="fa-regular fa-plus" size="small" @click="addItem" />
          </div>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col gap-4">
          <JobImportRow
            v-for="index in items.length"
            :key="index"
            v-model="items[index - 1]"
            :index="index - 1"
            :can-remove="items.length > 1"
            @remove="removeItem(index - 1)"
          />
        </div>
      </template>
    </Card>

    <Message v-if="validationErrors.length" severity="warn" :closable="false">
      <div class="flex flex-col gap-1">
        <strong>De import kan nog niet starten:</strong>
        <ul class="m-0 pl-5">
          <li v-for="(err, idx) in validationErrors" :key="idx" class="text-sm">
            {{ err }}
          </li>
        </ul>
      </div>
    </Message>

    <div class="flex justify-end">
      <Button
        v-permission="{ resource: AdminPermissionResource.Job, level: PermissionLevel.Write }"
        label="Start import"
        icon="fa-regular fa-wand-magic-sparkles"
        :disabled="!canSubmit"
        :loading="isSubmitting"
        @click="submit"
      />
    </div>
  </div>
</template>
